<script>
import { mapGetters } from 'vuex'
import TeamResource from '@/resources/team'
import BoardResource from '@/resources/board'
import CarResource from '@/resources/car'
import TeamRequestResource from '@/resources/teamRequest'
import DraftableRacerResource from '@/resources/draftableRacer'
import ResourceSearchInput from '../../components/ResourceSearchInput.vue'
import messages from '@/mixins/messages'
import lucra from '@/mixins/lucra'
import { FLAGS } from '@/config'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'AccountTeams',
    components: { ResourceSearchInput },
    mixins: [messages, lucra],
    computed: {
        ...mapGetters(['userId']),
        joinBoardId() {
            return this.$route.query.board || null
        },
        joinCashTournament() {
            return this.joinBoard ? this.joinBoard.cashTournament : null
        },
        alreadyOnJoinBoard() {
            return this.teamResources.some((team) => team.attributes.board === this.joinBoardId)
        },
        joinRegistration() {
            return (this.joinBoard && this.joinBoard.attributes.team_registration) || null
        },
        draftOffers() {
            return this.pendingRequests.filter((request) => request.attributes.kind === 'DRAFT' && request.attributes.user === this.userId && request.get('board') === this.joinBoardId)
        },
        selectedTeam() {
            return this.teamResources.find((team) => team.id === this.selectedTeamId) || this.teamResources.find((team) => team.attributes.board === this.joinBoardId) || this.teamResources[0] || null
        },
        requestedTeamIds() {
            return this.pendingRequests.filter((request) => request.attributes.kind === 'REQUEST' && request.attributes.user === this.userId).map((request) => request.attributes.team)
        },
    },
    watch: {
        joinBoardId() {
            this.loadJoinSection()
        },
    },
    data() {
        return {
            loading: true,
            teamResources: [],
            selectedTeamId: null,
            draftModalOpen: false,
            boardsById: {},
            flags: FLAGS,
            flagsCommon: { us: FLAGS.us, ca: FLAGS.ca, gb: FLAGS.gb },
            activeFlagTeamId: null,
            drafts: {},
            CarResource,
            saving: {},
            inviteTexts: {},
            inviting: {},
            joinLoading: false,
            joinBoard: null,
            joinBoardTeams: [],
            joiningTournamentBoardId: null,
            newTeamName: '',
            creatingTeam: false,
            joiningTeamId: null,
            joinMode: null,
            pendingRequests: [],
            draftablesByBoard: {},
            requestBusyId: null,
            draftingKey: null,
            togglingDraftable: false,
        }
    },
    methods: {
        timeDateFormattedPretty,
        isOwner(team) {
            return team.attributes.created_by === this.userId
        },
        boardFor(team) {
            return this.boardsById[team.attributes.board] || null
        },
        draft(team) {
            if (!this.drafts[team.id]) {
                this.$set(this.drafts, team.id, {
                    name: team.attributes.name,
                    flag_code: team.attributes.flag_code,
                    car: team.attributes.car_override,
                })
            }
            return this.drafts[team.id]
        },
        openFlagModal(team) {
            this.draft(team)
            this.activeFlagTeamId = team.id
            this.$refs.flagModal.show()
        },
        selectFlag(code) {
            this.drafts[this.activeFlagTeamId].flag_code = code
            this.$refs.flagModal.hide()
        },
        async load() {
            this.loading = true
            const teamResources = await TeamResource.filterAll({ members: this.userId, board__active: true })
            const boardIds = [...new Set(teamResources.map((team) => team.attributes.board))]
            const boards = await Promise.all(boardIds.map((id) => BoardResource.detail(id)))
            const boardsById = {}
            boards.forEach((board) => {
                boardsById[board.id] = board
            })
            this.boardsById = boardsById
            this.teamResources = teamResources.sort((a, b) => this.teamRecency(b) - this.teamRecency(a))
            this.loading = false
        },
        teamRecency(team) {
            const board = this.boardFor(team)
            if (!board) {
                return 0
            }
            const ended = board.attributes.completed || (board.attributes.expires_at && Date.parse(board.attributes.expires_at) < Date.now())
            return (ended ? 0 : 1e15) + (Date.parse(board.attributes.since || board.attributes.expires_at) || 0)
        },
        teamsLocked(team) {
            const board = this.boardFor(team)
            return !!(board && board.attributes.team_registration && board.attributes.team_registration.locked)
        },
        incomingRequests(team) {
            return this.pendingRequests.filter((request) => request.attributes.kind === 'REQUEST' && request.attributes.team === team.id)
        },
        draftablesFor(team) {
            return this.draftablesByBoard[team.attributes.board] || []
        },
        draftPending(team, userId) {
            return this.pendingRequests.some((request) => request.attributes.kind === 'DRAFT' && request.attributes.team === team.id && request.attributes.user === userId)
        },
        teamMemberNames(team) {
            return (team.get('members_detail') || []).map((member) => member.display_name).join(', ')
        },
        async loadRequests() {
            this.pendingRequests = await TeamRequestResource.filterAll({ status: 'PENDING' })
        },
        async loadDraftables() {
            const boardIds = [...new Set(this.teamResources.filter((team) => this.isOwner(team) && !team.get('is_full') && !this.teamsLocked(team)).map((team) => team.attributes.board))]
            const draftablesByBoard = {}
            for (const boardId of boardIds) {
                draftablesByBoard[boardId] = await DraftableRacerResource.filterAll({ board: boardId })
            }
            this.draftablesByBoard = draftablesByBoard
        },
        async refreshTeams() {
            await this.load()
            await this.loadRequests()
            await this.loadDraftables()
            if (this.joinBoardId) {
                await this.refreshBoardEntry(this.joinBoardId)
                this.joinBoardTeams = await TeamResource.filterAll({ board: this.joinBoardId })
            }
        },
        async acceptRequest(request) {
            this.requestBusyId = request.id
            try {
                await request.accept()
                this.addMessage('success', request.attributes.kind === 'DRAFT' ? `You're on ${request.get('team_name')}. Set laps at the simulator to climb the board.` : `${request.get('user_display_name')} is on your team.`)
                await this.refreshTeams()
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.requestBusyId = null
        },
        async declineRequest(request) {
            this.requestBusyId = request.id
            try {
                await request.decline()
                await this.loadRequests()
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.requestBusyId = null
        },
        async draftRacer(team, racer) {
            this.draftingKey = `${team.id}:${racer.attributes.user}`
            try {
                const result = await team.draft(racer.attributes.user)
                this.addMessage('success', result.status === 'ACCEPTED' ? `${racer} is on your team.` : `${racer} was drafted. They'll get an email to accept.`)
                await this.refreshTeams()
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.draftingKey = null
        },
        async setDraftable(on) {
            this.togglingDraftable = true
            try {
                if (on) {
                    await new DraftableRacerResource({ board: this.joinBoardId }).save()
                } else {
                    const mine = await DraftableRacerResource.filterAll({ board: this.joinBoardId, user: this.userId })
                    for (const racer of mine) {
                        await racer.delete()
                    }
                }
                await this.refreshBoardEntry(this.joinBoardId)
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.togglingDraftable = false
        },
        async acceptFromLink() {
            const requestId = this.$route.query.accept
            if (!requestId) {
                return
            }
            this.$router.replace({ query: { ...this.$route.query, accept: undefined } })
            const offer = this.pendingRequests.find((request) => request.id === requestId && request.attributes.kind === 'DRAFT' && request.attributes.user === this.userId)
            if (offer) {
                await this.acceptRequest(offer)
            } else {
                this.addMessage('info', 'That draft offer is no longer open.')
            }
        },
        canPickCar(team) {
            return this.isOwner(team) && team.get('car_choice') === 'TEAM' && !team.get('car_locked')
        },
        teamDraftChanged(team) {
            const d = this.draft(team)
            return d.name !== team.attributes.name || d.flag_code !== team.attributes.flag_code || (this.canPickCar(team) && d.car !== team.attributes.car_override)
        },
        async loadJoinSection() {
            if (!this.joinBoardId) {
                this.joinBoard = null
                this.joinBoardTeams = []
                return
            }
            this.joinLoading = true
            try {
                this.joinBoard = await BoardResource.detail(this.joinBoardId)
                this.joinBoardTeams = await TeamResource.filterAll({ board: this.joinBoardId })
            } catch (e) {
                this.addMessage('danger', 'We could not load that points series.')
                this.joinBoard = null
            }
            this.joinLoading = false
        },
        async refreshBoardEntry(boardId) {
            // useCache: false, since detail() otherwise serves the 10s client-side resource
            // cache, so this board (already cached from the initial page load) would come
            // back with the pre-join, not-yet-joined state.
            const board = await BoardResource.detail(boardId, { useCache: false })
            this.$set(this.boardsById, boardId, board)
            if (boardId === this.joinBoardId) {
                this.joinBoard = board
            }
            return board
        },
        // Shared by both places a racer can join a cash tournament from: the query-param-driven
        // join section above (a board they're not on a team for yet, or are mid-registering
        // for) and the per-team "join tournament" prompt below (a board they're already racing
        // for, but never went through the Lucra widget for, most often because staff added them
        // to the team directly instead of them registering themselves).
        async joinTournament(board) {
            this.joiningTournamentBoardId = board.id
            // Held back until the iframe is actually closed. It sits at z-index 3000 while
            // open, so a message added while it's still up renders underneath it, unseen.
            let pendingMessage = null
            try {
                await this.$lucraPrepareDeposit()
                await this.$lucraOpen(this.$refs.lucraContainer, { hidden: true })
                await this.$lucraLinkUser()
                let result = await this.$lucraJoinTournament(board.cashTournament.provider_matchup_id)
                if (result.needsDeposit) {
                    pendingMessage = ['info', 'Add funds to cover the buy-in, then select join again.']
                }
                if (result.locationError) {
                    pendingMessage = ['danger', 'We could not verify your location for a cash tournament. Try again with location services on.']
                } else if (result.joined) {
                    await board.joinCashTournament()
                    pendingMessage = ['success', 'You joined the tournament. Your results will now count toward payouts.']
                    await this.refreshBoardEntry(board.id)
                }
            } catch (e) {
                pendingMessage = ['danger', this.getResponseMessageFromAxiosException(e)]
            }
            await this.$lucraCloseRoute()
            if (pendingMessage) {
                this.addMessage(...pendingMessage)
            }
            this.joiningTournamentBoardId = null
        },
        async createTeamForBoard() {
            const name = this.newTeamName.trim()
            if (!name) {
                return
            }
            this.creatingTeam = true
            try {
                await TeamResource.register(this.joinBoardId, name)
                this.newTeamName = ''
                this.addMessage('success', "Team created. You're in. Invite your friends or draft racers below.")
                await this.refreshTeams()
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.creatingTeam = false
        },
        async joinTeamForBoard(team) {
            this.joiningTeamId = team.id
            try {
                const result = await team.join()
                this.addMessage('success', result.status === 'ACCEPTED' ? "You're on the team. Set laps at the simulator to climb the board." : `We let the owner of ${team.attributes.name} know you want to join. You'll see the team here once they accept.`)
                await this.refreshTeams()
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.joiningTeamId = null
        },
        async saveTeam(team) {
            const d = this.draft(team)
            this.$set(this.saving, team.id, true)
            try {
                team.set('name', d.name)
                team.set('flag_code', d.flag_code)
                if (this.canPickCar(team)) {
                    team.set('car_override', d.car)
                }
                await team.save()
                await this.load()
                this.addMessage('success', 'Team updated.')
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.$set(this.saving, team.id, false)
        },
        async sendInvites(team) {
            const text = this.inviteTexts[team.id] || ''
            const emails = text
                .split(/[\s,]+/)
                .map((e) => e.trim())
                .filter(Boolean)
            if (!emails.length) {
                return
            }
            this.$set(this.inviting, team.id, true)
            try {
                const result = await team.invite(emails)
                this.$set(this.inviteTexts, team.id, '')
                const invitedCount = (result.invited || []).length
                const skippedCount = (result.skipped || []).length
                let text = `Invited ${invitedCount} ${invitedCount === 1 ? 'friend' : 'friends'}.`
                if (skippedCount) {
                    text += ` ${skippedCount} could not be invited.`
                }
                this.addMessage('success', text)
            } catch (e) {
                this.addMessage('danger', this.getResponseMessageFromAxiosException(e))
            }
            this.$set(this.inviting, team.id, false)
        },
    },
    async mounted() {
        await Promise.all([this.load(), this.loadJoinSection(), this.loadRequests()])
        await this.loadDraftables()
        await this.acceptFromLink()
    },
}
</script>

<template>
    <div>
        <div class="mb-4">
            <h4 class="mb-1">Équipes</h4>
            <p class="text-muted mb-0">Gérez les équipes avec lesquelles vous participez aux séries à points.</p>
        </div>

        <div v-if="joinLoading" class="text-center py-4">
            <b-spinner />
        </div>

        <div v-else-if="joinBoard && alreadyOnJoinBoard && joinCashTournament && !joinCashTournament.joined" class="mb-5 pb-5 border-bottom">
            <h5 class="mb-1">Rejoindre le tournoi {{ joinBoard.attributes.name }}</h5>
            <p class="text-muted small mb-3">Vous participez déjà à cette série à points, mais vous n'avez pas encore rejoint son tournoi en argent réel. Vos résultats ne seront donc pas pris en compte pour les gains tant que vous ne l'aurez pas fait.</p>
            <b-card>
                <p class="small text-muted">Cela permet de vérifier votre identité afin que vous puissiez être payé si votre équipe se classe.</p>
                <b-button variant="primary" :disabled="joiningTournamentBoardId === joinBoard.id || !joinCashTournament.joinable" @click="joinTournament(joinBoard)">
                    <b-spinner small v-if="joiningTournamentBoardId === joinBoard.id" /> {{ lucraNeedsDeposit ? 'Ajouter des fonds et participer' : 'Rejoindre le tournoi' }}
                </b-button>
            </b-card>
        </div>

        <div v-else-if="joinBoard && !alreadyOnJoinBoard" class="mb-5 pb-5 border-bottom">
            <h5 class="mb-1">Rejoindre une équipe pour {{ joinBoard.attributes.name }}</h5>
            <p class="text-muted small mb-3" v-if="joinBoard.attributes.description">{{ joinBoard.attributes.description }}</p>

            <b-card class="mb-3" v-if="joinCashTournament">
                <h6 class="mb-2">Étape 1 : Rejoindre le tournoi</h6>
                <div v-if="joinCashTournament.joined" class="d-flex align-items-center"><b-icon icon="check-circle-fill" class="text-success mr-2" /> Vous participez au tournoi.</div>
                <template v-else>
                    <p class="small text-muted">Cela permet de vérifier votre identité afin que vous puissiez être payé si votre équipe se classe.</p>
                    <b-button variant="primary" :disabled="joiningTournamentBoardId === joinBoard.id || !joinCashTournament.joinable" @click="joinTournament(joinBoard)">
                        <b-spinner small v-if="joiningTournamentBoardId === joinBoard.id" /> {{ lucraNeedsDeposit ? 'Ajouter des fonds et participer' : 'Rejoindre le tournoi' }}
                    </b-button>
                </template>
            </b-card>

            <b-card>
                <h6 class="mb-2">{{ joinCashTournament ? 'Étape 2 : Intégrer une équipe' : 'Intégrer une équipe' }}</h6>
                <p v-if="joinRegistration && joinRegistration.locked" class="small text-muted mb-0"><b-icon icon="lock-fill" class="mr-1" />Les équipes sont verrouillées maintenant que la série a commencé.</p>
                <template v-else>
                    <div v-for="offer in draftOffers" :key="offer.id" class="d-flex flex-wrap align-items-center justify-content-between border rounded p-2 mb-3">
                        <span class="my-1 mr-2"><b-icon icon="person-plus-fill" class="text-success mr-1" /><strong>{{ offer.get('team_name') }}</strong> vous a sélectionné.</span>
                        <span class="my-1">
                            <b-button size="sm" variant="primary" class="mr-1" :disabled="requestBusyId === offer.id" @click="acceptRequest(offer)"> <b-spinner small v-if="requestBusyId === offer.id" /> Rejoindre l'équipe </b-button>
                            <b-button size="sm" variant="outline-secondary" :disabled="requestBusyId === offer.id" @click="declineRequest(offer)">Refuser</b-button>
                        </span>
                    </div>

                    <p class="small text-muted">Chaque équipe doit compter {{ joinBoard.attributes.team_size }} membres. Souhaitez-vous créer une équipe ou en rejoindre une ? Vous pouvez courir pour une seule équipe dans cette série.</p>
                    <div class="d-flex mb-3">
                        <b-button class="mr-2" :variant="joinMode === 'create' ? 'primary' : 'outline-primary'" @click="joinMode = 'create'">Créer une équipe</b-button>
                        <b-button :variant="joinMode === 'join' ? 'primary' : 'outline-primary'" @click="joinMode = 'join'">Rejoindre une équipe</b-button>
                    </div>

                    <template v-if="joinMode === 'create'">
                        <p v-if="joinRegistration && !joinRegistration.can_create" class="small text-muted"><b-icon icon="lock-fill" class="mr-1" />Cette série compte déjà le nombre maximal de {{ joinRegistration.max_teams }} équipes. Aucune nouvelle équipe ne peut donc être créée. Rejoignez-en une à la place.</p>
                        <div class="d-flex align-items-center">
                            <b-form-input v-model="newTeamName" placeholder="Nom de votre équipe" class="mr-2" :disabled="joinRegistration && !joinRegistration.can_create" />
                            <b-button variant="primary" class="text-nowrap" :disabled="creatingTeam || !newTeamName.trim() || (joinRegistration && !joinRegistration.can_create)" @click="createTeamForBoard"> <b-spinner small v-if="creatingTeam" /> Créer l'équipe </b-button>
                        </div>
                    </template>

                    <template v-else-if="joinMode === 'join'">
                        <b-form-checkbox switch class="mb-1" :checked="!!(joinRegistration && joinRegistration.draftable)" :disabled="togglingDraftable" @change="setDraftable">Autoriser les propriétaires d'équipe à me sélectionner</b-form-checkbox>
                        <p class="small text-muted">Les propriétaires d'équipe vous voient dans leur liste et peuvent vous sélectionner pour leur équipe. Nous vous enverrons un e-mail afin que vous puissiez accepter. Vous pouvez également demander à rejoindre une équipe ci-dessous.</p>

                        <div v-for="team in joinBoardTeams" :key="team.id" class="d-flex align-items-center justify-content-between border-top py-2">
                            <div class="mr-2">
                                <div class="font-weight-bold">
                                    {{ team.attributes.name }} <span class="text-muted small">({{ team.attributes.members.length }}/{{ joinBoard.attributes.team_size }})</span>
                                </div>
                                <div class="small text-muted">{{ teamMemberNames(team) || 'Aucun membre pour le moment' }}</div>
                            </div>
                            <b-badge v-if="team.get('is_full')" variant="light">Complète</b-badge>
                            <b-badge v-else-if="requestedTeamIds.includes(team.id)" variant="light">Demande envoyée</b-badge>
                            <b-button v-else size="sm" variant="secondary" class="text-nowrap" :disabled="joiningTeamId === team.id" @click="joinTeamForBoard(team)">
                                <b-spinner small v-if="joiningTeamId === team.id" /> {{ team.attributes.created_by ? 'Demander à rejoindre' : 'Rejoindre' }}
                            </b-button>
                        </div>
                        <p v-if="!joinBoardTeams.length" class="small text-muted mb-0">Aucune équipe pour le moment. Créez-en une ou autorisez les propriétaires d'équipe à vous sélectionner.</p>
                    </template>
                </template>
            </b-card>
        </div>

        <div v-if="loading" class="text-center py-5">
            <b-spinner />
        </div>

        <div v-else-if="!teamResources.length" class="sa-empty">
            Vous ne faites encore partie d'aucune équipe. Cherchez une série à points dont les inscriptions par équipe sont ouvertes sur la page
            <router-link :to="{ name: 'leaderboards' }">Classements</router-link>
            .
        </div>

        <div v-if="teamResources.length > 1" class="d-flex flex-wrap mb-4">
            <button v-for="team in teamResources" :key="team.id" type="button" class="sa-chip mr-2 mb-2" :class="{ 'is-active': selectedTeam && team.id === selectedTeam.id }" @click="selectedTeamId = team.id">
                {{ team.attributes.name }} &middot; {{ boardFor(team) ? boardFor(team).attributes.name : team.get('board_name') }}
            </button>
        </div>

        <div v-for="team in selectedTeam ? [selectedTeam] : []" :key="team.id" class="at-team mb-5">
            <!-- Series details -->
            <div class="mb-3">
                <h5 class="mb-1">
                    <router-link :to="{ name: 'leaderboards', params: { id: team.attributes.board } }">{{ boardFor(team) ? boardFor(team).attributes.name : team.get('board_name') }}</router-link>
                    <b-badge variant="primary" class="ml-2" v-if="isOwner(team)">Propriétaire</b-badge>
                    <b-badge variant="secondary" class="ml-2" v-if="team.get('is_full')">Équipe complète</b-badge>
                    <b-badge variant="secondary" class="ml-2" v-else>{{ team.get('open_slots') }} places disponibles</b-badge>
                </h5>
                <p class="text-muted small mb-1" v-if="boardFor(team) && boardFor(team).attributes.description">{{ boardFor(team).attributes.description }}</p>
                <div class="small text-muted">
                    Série à points &middot; Équipes de {{ team.get('team_size') }}
                    <template v-if="boardFor(team) && boardFor(team).attributes.since"> &middot; Depuis le {{ timeDateFormattedPretty(boardFor(team).attributes.since) }}</template>
                    <template v-if="boardFor(team) && boardFor(team).attributes.expires_at"> &middot; Se termine le {{ timeDateFormattedPretty(boardFor(team).attributes.expires_at) }}</template>
                </div>
                <div class="small mt-1" v-if="boardFor(team) && boardFor(team).cashTournament">
                    <b-badge variant="success" class="mr-1">Tournoi en argent réel</b-badge>
                    ${{ (boardFor(team).cashTournament.buy_in_amount_cents / 100).toFixed(2) }} de droit d'entrée, {{ boardFor(team).cashTournament.status.toLowerCase() }}, gains selon le résultat de l'équipe
                </div>
                <div class="small mt-2 d-flex align-items-center" v-if="boardFor(team) && boardFor(team).cashTournament && !boardFor(team).cashTournament.joined">
                    <b-icon icon="exclamation-triangle-fill" class="text-warning mr-2" />
                    <span class="mr-2">Vous n'avez pas encore rejoint ce tournoi en argent réel. Vos résultats ne seront donc pas pris en compte pour les gains.</span>
                    <b-button
                        size="sm"
                        variant="primary"
                        :disabled="joiningTournamentBoardId === team.attributes.board || !boardFor(team).cashTournament.joinable"
                        @click="joinTournament(boardFor(team))"
                    >
                        <b-spinner small v-if="joiningTournamentBoardId === team.attributes.board" /> {{ lucraNeedsDeposit ? 'Ajouter des fonds et participer' : 'Rejoindre le tournoi' }}
                    </b-button>
                </div>
            </div>

            <!-- Section 1: Team details -->
            <b-card class="mb-3">
                <h6 class="mb-3">Détails de l'équipe</h6>
                <template v-if="isOwner(team)">
                    <b-form-group label="Nom de l'équipe :">
                        <b-input-group>
                            <b-form-input type="text" v-model="draft(team).name" autocomplete="new-password" />
                            <template slot="append">
                                <b-input-group-text>
                                    <img :src="`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.2.1/flags/1x1/${draft(team).flag_code}.svg`" class="flag" />
                                </b-input-group-text>
                                <b-button variant="outline-primary" @click="openFlagModal(team)">Changer de pays</b-button>
                            </template>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group v-if="canPickCar(team)" label="Voiture de l'équipe :" :description="team.get('car_group_name') ? `Choisissez parmi la catégorie ${team.get('car_group_name')}. Chaque membre conduit cette voiture dans les courses classées, et elle est verrouillée dès que la première course classée a lieu.` : 'Chaque membre conduit cette voiture dans les courses classées, et elle est verrouillée dès que la première course classée a lieu.'">
                        <resource-search-input :to="CarResource" v-model="draft(team).car" primary-key :filter="() => (team.get('car_group') ? { groups: team.get('car_group') } : {})" placeholder="Choisir une voiture" />
                    </b-form-group>
                    <b-button variant="primary" size="sm" :disabled="saving[team.id] || !teamDraftChanged(team)" @click="saveTeam(team)"> <b-spinner small v-if="saving[team.id]" /> Enregistrer l'équipe </b-button>
                </template>
                <template v-else>
                    <div class="d-flex align-items-center">
                        <img :src="team.get('flag_url')" class="flag mr-2" />
                        <strong>{{ team.attributes.name }}</strong>
                    </div>
                </template>
                <p v-if="team.get('car') && !canPickCar(team)" class="text-muted small mt-3 mb-0">
                    <b-icon icon="lock-fill" v-if="team.get('car_locked')" class="mr-1" />Voiture de l'équipe : <strong>{{ team.get('car_name') }}</strong>
                </p>
            </b-card>

            <!-- Section 2: Members -->
            <b-card class="mb-3">
                <div class="d-flex align-items-center justify-content-between mb-3">
                    <h6 class="mb-0">Membres ({{ team.attributes.members.length }}/{{ team.get('team_size') }})</h6>
                    <b-button v-if="isOwner(team) && !team.get('is_full') && !teamsLocked(team)" size="sm" variant="outline-primary" @click="draftModalOpen = true"><b-icon icon="person-plus" class="mr-1" />Sélectionner des pilotes</b-button>
                </div>
                <b-table-simple small responsive class="mb-0">
                    <b-tbody>
                        <b-tr v-for="member in team.get('members_detail')" :key="member.id">
                            <b-td>
                                {{ member.display_name }}
                                <b-badge variant="light" class="ml-1" v-if="member.id === userId">Vous</b-badge>
                                <b-badge variant="light" class="ml-1" v-if="member.id === team.attributes.created_by">Propriétaire</b-badge>
                            </b-td>
                            <b-td class="text-right font-weight-bold">{{ member.points }} pts</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-card>

            <b-card class="mb-3" v-if="isOwner(team) && incomingRequests(team).length">
                <h6 class="mb-3">Demandes d'adhésion</h6>
                <div v-for="request in incomingRequests(team)" :key="request.id" class="d-flex align-items-center justify-content-between mb-2">
                    <span class="mr-2">{{ request.get('user_display_name') }}</span>
                    <span class="text-nowrap">
                        <b-button size="sm" variant="primary" class="mr-1" :disabled="requestBusyId === request.id || team.get('is_full') || teamsLocked(team)" @click="acceptRequest(request)"> <b-spinner small v-if="requestBusyId === request.id" /> Accepter </b-button>
                        <b-button size="sm" variant="outline-secondary" :disabled="requestBusyId === request.id" @click="declineRequest(request)">Refuser</b-button>
                    </span>
                </div>
            </b-card>

            <b-modal v-model="draftModalOpen" title="Sélectionner des pilotes" scrollable hide-footer no-fade centered>
                <p class="small text-muted mb-3">Pilotes à la recherche d'une équipe dans cette série. Les pilotes sélectionnés reçoivent un e-mail pour accepter.</p>
                <div v-for="racer in draftablesFor(team)" :key="racer.id" class="d-flex align-items-center justify-content-between mb-2">
                    <span class="mr-2">{{ racer.get('display_name') }}</span>
                    <b-badge v-if="draftPending(team, racer.attributes.user)" variant="light">Sélectionné</b-badge>
                    <b-button v-else size="sm" variant="primary" :disabled="draftingKey === `${team.id}:${racer.attributes.user}`" @click="draftRacer(team, racer)">
                        <b-spinner small v-if="draftingKey === `${team.id}:${racer.attributes.user}`" /> Sélectionner
                    </b-button>
                </div>
                <p v-if="!draftablesFor(team).length" class="small text-muted mb-0">Personne ne recherche d'équipe actuellement.</p>
            </b-modal>

            <!-- Section 3: Invite more friends -->
            <b-card v-if="isOwner(team) && !teamsLocked(team)">
                <h6 class="mb-3">Inviter plus d'amis</h6>
                <label class="small text-muted mb-1">Une adresse e-mail par ligne, ou séparées par des virgules</label>
                <b-form-textarea v-model="inviteTexts[team.id]" rows="2" placeholder="ami@exemple.com" />
                <b-button size="sm" variant="primary" class="mt-2" :disabled="inviting[team.id] || !(inviteTexts[team.id] || '').trim()" @click="sendInvites(team)"> <b-spinner small v-if="inviting[team.id]" /> Envoyer les invitations </b-button>
            </b-card>
        </div>

        <b-modal ref="flagModal" title="Changer de pays" scrollable hide-footer no-fade centered>
            <b-list-group class="mb-3">
                <b-list-group-item href="javascript:{}" v-for="(flag, code) in flagsCommon" :key="code" @click="selectFlag(code)"> <img :src="`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.2.1/flags/1x1/${code}.svg`" class="flag" /> {{ flag }} </b-list-group-item>
            </b-list-group>
            <b-list-group>
                <b-list-group-item href="javascript:{}" v-for="(flag, code) in flags" :key="code" @click="selectFlag(code)"> <img :src="`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.2.1/flags/1x1/${code}.svg`" class="flag" /> {{ flag }} </b-list-group-item>
            </b-list-group>
        </b-modal>

        <div ref="lucraContainer" class="at-lucra"></div>
    </div>
</template>

<style lang="scss" scoped>
img.flag {
    height: 24px;
    border-radius: 12px;
    border: 1px solid #fff;
}
.at-lucra {
    position: fixed;
    inset: 0;
    z-index: 3000;
}
.at-lucra:empty {
    display: none;
}
</style>
